import { Badge, Box, Card, Group, SimpleGrid, Stack } from '@mantine/core';
import { IconAlertTriangle, IconBan, IconPlugConnectedX, IconQuestionMark } from '@tabler/icons-react';
import { GetEvChargingStationResponse } from 'api/actions/get-ev-charging-station/get-ev-charging-station-response';
import { StationLiveStatus } from 'pages/ev-charging-station/components/StationHeaderCard';
import useLang from 'lang';
import sprintf from 'lang/sprintf';
import P2Medium from 'theme/components/Typography/P2Medium';
import P4Medium from 'theme/components/Typography/P4Medium';
import P4Regular from 'theme/components/Typography/P4Regular';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';

interface AvailableStationCardProps {
  station: GetEvChargingStationResponse;
  /** Live AgeVolt status; 'charging' is rendered by ChargingStationCard instead. */
  liveStatus: StationLiveStatus;
}

/**
 * Shortens an AgeVolt plug type such as "Type 2 Connector (Cable Attached)"
 * to its standard name, e.g. "Type 2", and tells whether the cable is attached.
 */
function parsePlugType(plugType: string) {
  const cableAttached = /cable attached/i.test(plugType);
  const name = plugType
    .replace(/\(.*?\)/g, '')
    .replace(/\bconnector\b/i, '')
    .trim();

  return { name: name || plugType, cableAttached };
}

/**
 * Station name/status and a spec-tile summary (charger type, max power,
 * price per kWh, and connector type), used whenever the current customer is
 * not charging here.
 *
 * The connector tile replaced the original "payment options" tile: AgeVolt
 * does not report payment methods, while the plug type is known.
 */
export default function AvailableStationCard({ station, liveStatus }: AvailableStationCardProps) {
  const { _t, lang } = useLang();
  const pricePerKwhWithVat = station.pricePerKwhBeforeVat * (1 + station.vatPercentage / 100);
  const { status, connectorCount, availableConnectorCount, powerType, maxPowerKw, plugType } = liveStatus;
  const plug = plugType ? parsePlugType(plugType) : null;

  let statusLabel = _t('Status unavailable');
  let statusIcon = <IconQuestionMark size={14} color="var(--mantine-color-gray-6)" />;

  if (status === 'available') {
    statusLabel =
      connectorCount && connectorCount > 1 && availableConnectorCount !== null
        ? sprintf(_t('%d of %d available'), availableConnectorCount, connectorCount)
        : _t('Available');
    statusIcon = <Box w={10} h={10} bg="green.6" style={{ borderRadius: '50%' }} />;
  } else if (status === 'occupied' || status === 'charging') {
    statusLabel = _t('Occupied');
    statusIcon = <IconBan size={14} color="var(--mantine-color-red-6)" />;
  } else if (status === 'faulted') {
    statusLabel = _t('Fault');
    statusIcon = <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />;
  } else if (status === 'offline') {
    statusLabel = _t('Offline');
    statusIcon = <IconPlugConnectedX size={14} color="var(--mantine-color-gray-6)" />;
  }

  return (
    <Stack gap={8} px={16} mt={-8} style={{ position: 'relative' }}>
      <Card p={16} radius={10}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={0}>
            <H2SemiBold>
              {station.name}
              {station.connectorCount !== null ? ` (${station.connectorCount})` : ''}
            </H2SemiBold>
            <P4Regular color="gray.7">{station.buildingDisplay || _t('Location not set')}</P4Regular>
          </Stack>
          <Badge
            radius={20}
            variant="outline"
            color="gray.2"
            styles={{ label: { textTransform: 'none' } }}
            px={12}
            py={14}
            style={{ flexShrink: 0 }}
            leftSection={statusIcon}
          >
            <P4Medium color="dark">{statusLabel}</P4Medium>
          </Badge>
        </Group>
      </Card>

      <Card p={16} radius={10}>
        <SimpleGrid cols={2} spacing={16} verticalSpacing={16}>
          <Stack gap={0}>
            <P2Medium>{powerType ?? '–'}</P2Medium>
            <P4Regular color="gray.6">{_t('Charger type')}</P4Regular>
          </Stack>
          <Stack gap={0}>
            <P2Medium>
              {maxPowerKw !== null ? `${maxPowerKw.toLocaleString(lang, { maximumFractionDigits: 1 })} kW` : '–'}
            </P2Medium>
            <P4Regular color="gray.6">{_t('Max power')}</P4Regular>
          </Stack>
          <Stack gap={0}>
            <P2Medium>
              {pricePerKwhWithVat.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </P2Medium>
            <P4Regular color="gray.6">{_t('Price per kWh')}</P4Regular>
          </Stack>
          <Stack gap={0}>
            <P2Medium>{plug ? `${plug.name}${plug.cableAttached ? ` ${_t('with cable')}` : ''}` : '–'}</P2Medium>
            <P4Regular color="gray.6">{_t('Connector type')}</P4Regular>
          </Stack>
        </SimpleGrid>
      </Card>
    </Stack>
  );
}
